import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Megaphone, Calendar, Sparkles, Share2, Search, X, Tag as TagIcon,
  Ticket, Percent, ArrowRight,
} from 'lucide-react';
import { api, municipiosApi } from '../services/api';
import Breadcrumbs from '../components/Breadcrumbs';
import useTenantGeoRedirect from '../hooks/useTenantGeoRedirect';
import useSiteConfig from '../hooks/useSiteConfig';

const TIPO_LABELS = {
  data_comemorativa: 'Data comemorativa',
  categoria_tematica: 'Categoria temática',
  evento_municipal: 'Evento municipal',
  sazonalidade: 'Sazonalidade',
  impacto_social: 'Impacto social',
  parceria_institucional: 'Parceria',
};

const PERIODO_OPTIONS = [
  { value: 'ativas', label: 'Em andamento' },
  { value: 'futuras', label: 'Vai começar' },
  { value: 'encerradas', label: 'Encerradas' },
  { value: 'todas', label: 'Todas' },
];

const SORT_OPTIONS = [
  { value: 'relevancia', label: 'Relevância' },
  { value: 'terminando', label: 'Terminando em breve' },
  { value: 'recentes', label: 'Mais recentes' },
  { value: 'compartilhadas', label: 'Mais compartilhadas' },
  { value: 'adesoes', label: 'Mais aderidas' },
];

function fmtDate(s) {
  return (s || '').slice(0, 10).split('-').reverse().join('/');
}

function diasRestantes(data_fim) {
  if (!data_fim) return null;
  const fim = new Date(data_fim);
  const now = new Date();
  const ms = fim.getTime() - now.getTime();
  if (ms <= 0) return 'encerrada';
  const dias = Math.ceil(ms / (1000 * 60 * 60 * 24));
  if (dias === 1) return 'termina hoje';
  if (dias <= 7) return `termina em ${dias} dias`;
  return null;
}

function FeaturedCampaignCard({ c }) {
  const cor = c.cor_destaque || '#111827';
  const urgencia = c.status_efetivo === 'ativa' ? diasRestantes(c.data_fim) : null;
  return (
    <Link
      to={`/campanhas/${c.id}`}
      data-testid={`campanha-destaque-${c.id}`}
      className="group block rounded-3xl overflow-hidden mb-8 relative shadow-xl hover:shadow-2xl transition-shadow"
    >
      <div className="relative h-72 md:h-96 overflow-hidden">
        {c.arte_visual_url ? (
          <img
            src={c.arte_visual_url}
            alt={c.nome}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div
            className="w-full h-full"
            style={{ background: `linear-gradient(135deg, ${cor}, ${cor}cc)` }}
          />
        )}
        {/* Overlay gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, transparent 0%, transparent 35%, ${cor}f5 100%)`,
          }}
        />
        {/* Top-left destaque badge */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 text-amber-950 text-xs font-bold uppercase tracking-wider shadow-md">
            <Sparkles className="w-3.5 h-3.5" />
            Campanha em destaque
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/90 text-gray-800 backdrop-blur-sm">
            {TIPO_LABELS[c.tipo] || c.tipo}
          </span>
        </div>
        {/* Bottom content */}
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-white">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight drop-shadow-md">
            {c.nome}
          </h2>
          {c.tema && (
            <p className="mt-2 text-base md:text-lg text-white/90 max-w-3xl line-clamp-2">
              {c.tema}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
              <Calendar className="w-3.5 h-3.5" />
              {fmtDate(c.data_inicio)} → {fmtDate(c.data_fim)}
            </span>
            {urgencia && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500 text-white font-semibold animate-pulse">
                ⏰ {urgencia}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5" />
              <strong>{c.total_adesoes || 0}</strong> participando
            </span>
            <span className="ml-auto inline-flex items-center gap-1 text-white font-semibold group-hover:gap-2 transition-all">
              Saiba mais <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function CampaignCard({ c }) {
  const cor = c.cor_destaque || '#6D28D9';
  const urgencia = c.status_efetivo === 'ativa' ? diasRestantes(c.data_fim) : null;
  const status = c.status_efetivo;
  const statusColor = {
    ativa: 'bg-green-100 text-green-700',
    publicada: 'bg-blue-100 text-blue-700',
    encerrada: 'bg-gray-200 text-gray-600',
  }[status] || 'bg-gray-100 text-gray-700';

  return (
    <Link
      to={`/campanhas/${c.id}`}
      data-testid={`campanha-list-card-${c.id}`}
      className="group block rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
    >
      {/* Banner */}
      <div className="relative h-40 overflow-hidden">
        {c.arte_visual_url ? (
          <img
            src={c.arte_visual_url}
            alt={c.nome}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${cor}, ${cor}aa)` }}
          >
            <Megaphone className="w-16 h-16 text-white/40" />
          </div>
        )}
        <div className="absolute inset-x-0 top-0 h-1.5" style={{ background: cor }} />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-black/40 text-white backdrop-blur-sm"
          >
            {TIPO_LABELS[c.tipo] || c.tipo}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-full ${statusColor}`}>
            {status}
          </span>
        </div>
        {urgencia && (
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 text-white text-[10px] font-semibold uppercase tracking-wider animate-pulse">
              ⏰ {urgencia}
            </span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-bold text-lg leading-tight line-clamp-2 group-hover:text-purple-700 transition-colors">
            {c.nome}
          </h3>
          {c.tema && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-1">{c.tema}</p>
          )}
        </div>

        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Calendar className="w-3 h-3 shrink-0" />
          <span>{fmtDate(c.data_inicio)} → {fmtDate(c.data_fim)}</span>
        </div>

        <div className="flex flex-wrap gap-1.5 text-[10px]">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 text-purple-700">
            <Sparkles className="w-3 h-3" />
            <strong>{c.total_adesoes || 0}</strong> participando
          </span>
          {c.shares_total > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-50 text-pink-700">
              <Share2 className="w-3 h-3" />
              <strong>{c.shares_total}</strong>
            </span>
          )}
          {c.municipio_nome && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              📍 {c.municipio_nome}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function CampanhasLista() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const location = useLocation();
  // Auto-redirect /campanhas → /<slug>/campanhas (user choice → geo)
  useTenantGeoRedirect({ routeName: 'campanhas', currentSlug: municipioSlug });
  const { config: siteConfig } = useSiteConfig();

  const [campanhas, setCampanhas] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ tipo: {} });
  const [loading, setLoading] = useState(true);
  const [municipio, setMunicipio] = useState(null);
  const [ofertas, setOfertas] = useState([]);

  const [filterTipo, setFilterTipo] = useState('all');
  const [filterPeriodo, setFilterPeriodo] = useState('ativas');
  const [sort, setSort] = useState('relevancia');
  const [search, setSearch] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [municipioSlug, location.pathname]);

  useEffect(() => {
    if (!municipioSlug) { setMunicipio(null); return; }
    municipiosApi.list()
      .then((all) => {
        const found = (all || []).find((m) => m.slug === municipioSlug);
        if (found) setMunicipio(found);
      })
      .catch(() => {});
  }, [municipioSlug]);

  useEffect(() => {
    const params = { periodo: filterPeriodo, sort, limit: 60 };
    if (municipioSlug) params.municipio_slug = municipioSlug;
    if (filterTipo !== 'all') params.tipo = filterTipo;
    setLoading(true);
    api.get('/v2/campanhas/publico', { params })
      .then((r) => {
        setCampanhas(r.data?.items || []);
        setTotal(r.data?.total || 0);
        setFacets(r.data?.facets || { tipo: {} });
      })
      .catch(() => {
        setCampanhas([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [municipioSlug, filterTipo, filterPeriodo, sort]);

  useEffect(() => {
    const params = {};
    if (municipioSlug) params.municipio_slug = municipioSlug;
    api.get('/v2/promotions/public', { params })
      .then((r) => setOfertas(r.data || []))
      .catch(() => setOfertas([]));
  }, [municipioSlug]);

  const filtered = useMemo(() => {
    if (!search.trim()) return campanhas;
    const q = search.trim().toLowerCase();
    return campanhas.filter((c) =>
      c.nome?.toLowerCase().includes(q) ||
      c.tema?.toLowerCase().includes(q) ||
      c.descricao?.toLowerCase().includes(q)
    );
  }, [campanhas, search]);

  const breadcrumbItems = [
    { label: 'Promoções' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/50 to-white" data-testid="campanhas-publico-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-700 via-purple-600 to-fuchsia-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px, 60px 60px',
          }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-12 md:py-16">
          <Breadcrumbs items={breadcrumbItems} municipio={municipio} className="mb-4" />
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm mb-4">
              <Megaphone className="w-4 h-4" />
              <span className="text-xs uppercase font-semibold tracking-wider">
                {siteConfig?.promocoes_hero_tag || 'Comunidade de Promoções'}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold leading-tight">
              {siteConfig?.promocoes_hero_titulo || 'Promoções em'}{' '}
              <span className="text-amber-300">
                {municipio?.nome || 'andamento'}
              </span>
            </h1>
            <p className="text-white/90 text-base md:text-lg mt-3 max-w-2xl">
              {siteConfig?.promocoes_hero_subtitulo || 'Descubra ações coletivas dos negócios locais e aproveite benefícios exclusivos em datas comemorativas, eventos e ações sazonais.'}
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-sm">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm">
                <Sparkles className="w-4 h-4" />
                <span><strong>{total}</strong> promoção(ões)</span>
              </div>
              {ofertas.length > 0 && (
                <button
                  type="button"
                  onClick={() => document.getElementById('ofertas-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 transition text-white font-medium"
                  data-testid="hero-ofertas-pill"
                >
                  <Ticket className="w-4 h-4" />
                  <span><strong>{ofertas.length}</strong> oferta(s)</span>
                </button>
              )}
              {Object.keys(facets.tipo || {}).length > 0 && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm">
                  <TagIcon className="w-4 h-4" />
                  <span><strong>{Object.keys(facets.tipo).length}</strong> tipo(s) de promoção</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Toolbar de filtros */}
        <div className="mt-4 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              data-testid="campanhas-search"
              placeholder="Buscar por nome, tema ou descrição…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none text-sm bg-white"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Limpar busca"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            data-testid="campanhas-filter-periodo"
            value={filterPeriodo}
            onChange={(e) => setFilterPeriodo(e.target.value)}
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm cursor-pointer hover:border-purple-300"
          >
            {PERIODO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            data-testid="campanhas-filter-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm cursor-pointer hover:border-purple-300"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>Ordenar: {o.label}</option>
            ))}
          </select>
        </div>

        {/* Chips de tipo (facetas) */}
        {Object.keys(facets.tipo || {}).length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            <button
              onClick={() => setFilterTipo('all')}
              data-testid="campanhas-tipo-chip-all"
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filterTipo === 'all'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
              }`}
            >
              Todos {filterTipo === 'all' && `(${total})`}
            </button>
            {Object.entries(facets.tipo).map(([tipo, count]) => (
              <button
                key={tipo}
                onClick={() => setFilterTipo(tipo)}
                data-testid={`campanhas-tipo-chip-${tipo}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  filterTipo === tipo
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                }`}
              >
                {TIPO_LABELS[tipo] || tipo} <span className="opacity-70">({count})</span>
              </button>
            ))}
            {ofertas.length > 0 && (
              <button
                type="button"
                onClick={() => document.getElementById('ofertas-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                data-testid="campanhas-tipo-chip-ofertas"
                className="text-xs px-3 py-1.5 rounded-full border bg-amber-500 text-white border-amber-500 hover:bg-amber-600 transition inline-flex items-center gap-1"
              >
                <Ticket className="w-3 h-3" />
                Ofertas <span className="opacity-80">({ofertas.length})</span>
              </button>
            )}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-2xl bg-white overflow-hidden border border-gray-100">
                <div className="h-40 bg-gray-100 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-5 bg-gray-100 rounded animate-pulse w-3/4" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700">
              {search ? 'Nenhuma promoção encontrada' : 'Nenhuma promoção por aqui'}
            </h3>
            <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
              {search
                ? 'Tente outra palavra-chave ou ajuste os filtros.'
                : 'Volte em breve — novas promoções são lançadas regularmente.'}
            </p>
            {(filterPeriodo !== 'todas' || filterTipo !== 'all' || search) && (
              <button
                onClick={() => { setFilterPeriodo('todas'); setFilterTipo('all'); setSearch(''); }}
                className="mt-4 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium"
                data-testid="campanhas-reset-filters"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {filtered[0]?.destaque_principal && (
              <FeaturedCampaignCard c={filtered[0]} />
            )}
            <div
              data-testid="campanhas-grid"
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8"
            >
              {(filtered[0]?.destaque_principal ? filtered.slice(1) : filtered).map((c) => (
                <CampaignCard key={c.id} c={c} />
              ))}
            </div>
          </>
        )}

        {/* Bloco de Ofertas (cupons / vouchers) */}
        {ofertas.length > 0 && (
          <section id="ofertas-section" className="mt-10 mb-10" data-testid="ofertas-block">
            <div className="mb-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Ticket className="w-6 h-6 text-amber-500" />
                Ofertas <span className="text-base font-medium text-gray-500">({ofertas.length})</span>
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Cupons e descontos exclusivos para resgatar agora
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ofertas.map((o) => (
                <Link
                  key={o.id}
                  to={`/cupom/${o.codigo}`}
                  data-testid={`oferta-card-${o.id}`}
                  className="group block rounded-xl bg-white border border-gray-100 hover:border-amber-300 hover:shadow-md transition p-4"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-500 line-clamp-1">
                      {o.negocio_nome}
                    </span>
                    <span className="shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold">
                      {o.tipo === 'percentual' ? (
                        <><Percent className="w-3 h-3" />{o.desconto}%</>
                      ) : (
                        <>R${o.preco_promocional}</>
                      )}
                    </span>
                  </div>
                  <h3 className="font-semibold text-sm leading-tight line-clamp-2 group-hover:text-amber-700 transition-colors">
                    {o.titulo}
                  </h3>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                    {o.descricao}
                  </p>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="font-mono font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                      {o.codigo}
                    </span>
                    <span className="text-amber-600 font-medium inline-flex items-center gap-0.5">
                      Resgatar <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

    </div>
  );
}
